import React, { useEffect, useRef, useState } from 'react';
import '../styles/legal-process-timeline.css';

interface ProcessStep {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: string;
}

interface LegalProcessTimelineProps {
  title?: string;
  subtitle?: string;
  description?: string;
  sectionNumber?: string;
  steps?: ProcessStep[];
}

const LegalProcessTimeline: React.FC<LegalProcessTimelineProps> = ({
  title = "Legal Process Timeline",
  subtitle = "Your Journey to Justice",
  description = "A clear, step-by-step guide to what you can expect when working with us.",
  sectionNumber = "0005",
  steps = [
    {
      id: "1",
      number: "01",
      title: "Initial Consultation",
      description: "We meet with you to understand your case, evaluate your claim, and explain your legal options. This consultation is completely free and confidential.",
      icon: "📋"
    },
    {
      id: "2",
      number: "02",
      title: "Investigation",
      description: "Our team gathers evidence, reviews medical records, interviews witnesses, and builds a strong foundation for your case.",
      icon: "🔍"
    },
    {
      id: "3",
      number: "03",
      title: "Case Filing",
      description: "We prepare and file all necessary legal documents, officially initiating your claim and ensuring all deadlines are met.",
      icon: "⚖️"
    },
    {
      id: "4",
      number: "04",
      title: "Negotiation",
      description: "We negotiate aggressively with the opposing party and insurance companies to secure a fair settlement that reflects the true value of your case.",
      icon: "💼"
    },
    {
      id: "5",
      number: "05",
      title: "Resolution",
      description: "Whether through settlement or trial verdict, we fight to achieve the best possible outcome and ensure you receive your compensation.",
      icon: "✓"
    }
  ]
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  useEffect(() => {
    // Auto-advance through steps when timeline is in view
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const triggerPoint = viewportHeight * 0.5;
      
      // Check if timeline is in view
      if (rect.top < triggerPoint && rect.bottom > 0) {
        // Calculate which step based on scroll position
        const sectionProgress = (triggerPoint - rect.top) / (viewportHeight * 0.8);
        const progress = Math.max(0, Math.min(1, sectionProgress));
        const currentStep = Math.min(Math.floor(progress * steps.length), steps.length - 1);
        setActiveStepIndex(currentStep);
      } else if (rect.top >= triggerPoint) {
        setActiveStepIndex(0);
      } else {
        setActiveStepIndex(steps.length - 1);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [steps.length]);

  return (
    <div 
      ref={containerRef}
      className="legal-process-container"
    >
      <div className="legal-process-sticky">
        {/* Header Section */}
        <div className="legal-process-header">
          <div className="legal-process-subtitle-section">
            <h1 className="legal-process-subtitle">{subtitle}</h1>
            <p className="legal-process-description">{description}</p>
          </div>
        </div>

        {/* Timeline */}
        <div ref={timelineRef} className="timeline">
          <div className="timeline-progress-bar">
            <div 
              className="timeline-progress-fill"
              style={{
                // Bar fills to the current active step position
                height: `${((activeStepIndex + 1) / steps.length) * 100}%`
              }}
            />
          </div>

          <div className="timeline-steps">
            {steps.map((step, index) => {
              const isActive = index === activeStepIndex;
              const isComplete = index < activeStepIndex;
              const isVisible = index <= activeStepIndex;

              return (
                <div
                  key={step.id}
                  className={`timeline-step ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''}`}
                  style={{
                    opacity: isVisible ? 1 : 0.3,
                    transform: `translateX(${isVisible ? '0' : '-20px'})`
                  }}
                >
                  <div className="timeline-step-marker">
                    <div className="timeline-step-icon">{step.icon}</div>
                    <div className="timeline-step-number">{step.number}</div>
                  </div>
                  <div className="timeline-step-content">
                    <h3 className="timeline-step-title">{step.title}</h3>
                    <p className="timeline-step-description">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalProcessTimeline;

